/*
 * State → look for the network console (design/network-console/proto/src/lib/states.ts).
 * Rule (handoff §2): a state reads without colour — glyph + word + border style
 * (solid = settled, dashed = in progress, dotted = waiting). Colour only repeats it.
 */
import type { StateColor } from '../../live-ui/ui'
import type { NodeInfo, OutputState } from '../../api'
import type { HopState, LinkState, NodeRelay, NodeRelayState, NoPathReason, RelayEpoch, RelayLink, RelayRoute, RouteState } from './api'

export type Border = 'solid' | 'dashed' | 'dotted'

export interface Spec {
  glyph: string
  word: string
  color: StateColor
  border: Border
  /** one line for tooltips and screen readers */
  hint?: string
}

const s = (glyph: string, word: string, color: StateColor, border: Border, hint?: string): Spec => ({ glyph, word, color, border, hint })

/* ── nodes ──────────────────────────────────────────────────────────────── */

export type NodeView = 'online' | 'degraded' | 'offline' | 'revoked' | 'not_in_peers'

export const NODE_SPEC: Record<NodeView, Spec> = {
  online: s('●', 'ONLINE', 'live', 'solid', 'On line, reports every 5 s'),
  degraded: s('▲', 'DEGRADED', 'warn', 'solid', 'On line, but a link, the clock or the load needs attention'),
  offline: s('✕', 'NO LINK', 'fail', 'solid', 'The center has no reports from this node'),
  revoked: s('■', 'REVOKED', 'idle', 'solid', 'Revoked: removed from the peers list'),
  not_in_peers: s('○', 'NOT IN PEERS', 'idle', 'dotted', 'Relay key not yet acknowledged by the network'),
}

/** The drain of a node's process (MOVE-1): no new streams, live ones go on. */
export const DRAIN_SPEC = {
  // idle · dotted as on the ConsoleNodeTable card (owner 30.10, AUDIT NG-01 A); was warn · dashed
  draining: s('◐', 'DRAINING', 'idle', 'dotted', 'Takes no new streams; live ones go on until they end or the deadline, then move out one by one'),
  drained: s('◯', 'DRAINED', 'idle', 'solid', 'Nothing left on the node: ready for an update or maintenance'),
} satisfies Record<'draining' | 'drained', Spec>

/** The failover mode of a node (C0-DNS-1 v2). */
export const FAILOVER_SPEC = {
  none: s('○', 'NO FAILOVER', 'idle', 'dotted', 'The DNS name always points at this node'),
  backup: s('◐', 'BACKUP', 'live', 'solid', 'A designated backup node takes the name when this one fails or is updated'),
  group: s('◍', 'GROUP', 'live', 'solid', 'The live members of the region group take the name'),
  cluster: s('◎', 'CLUSTER', 'idle', 'solid', 'One shared address (C1): nothing moves yet'),
} satisfies Record<'none' | 'backup' | 'group' | 'cluster', Spec>

/** A host the center is installing over SSH (M2.4): no node record yet. */
export const INSTALLING_SPEC = s('◔', 'INSTALLING', 'start', 'dashed', 'The center is installing this node over SSH')

/** The host agent of a node (M2.4). */
export const AGENT_SPEC = {
  on: s('●', 'AGENT ON', 'live', 'solid', 'The host agent is connected'),
  off: s('✕', 'AGENT OFF', 'fail', 'solid', 'The host agent is not connected'),
  none: s('─', 'MANUAL', 'idle', 'solid', 'Installed by hand: no host agent'),
} satisfies Record<string, Spec>

/** One issue of a degraded node, explained (Overview › Why degraded). */
export function issueWhy(issue: string, n: Pick<NodeInfo, 'relay' | 'clock'>): string {
  if (issue.startsWith('CPU')) return 'at or above 85%; the node may start dropping frames, new routes should avoid it.'
  if (issue.startsWith('clock')) {
    const c = n.clock
    return `offset from the center over 20 ms or not synced${c ? ` (now ${c.offset_ms.toFixed(1)} ms${c.rtt_ms !== undefined ? `, rtt ${Math.round(c.rtt_ms)} ms` : ''}${c.synced === false ? ', kernel clock not synced' : ''})` : ''}. Hop latency through this node is shown but marked suspect; routing is not affected.`
  }
  if (issue.includes('legs')) return 'some streams on this node wait for a hop to come back; see Links and Streams.'
  if (issue === 'not in peers') return 'other nodes have not acknowledged its relay key yet; routes do not use it.'
  if (issue === 'routes not applied') return `routes v${n.relay?.routes_version_acked ?? 0} applied, v${n.relay?.routes_version ?? 0} sent; the node has not confirmed the latest legs.`
  return ''
}

/** Why a node is degraded, in words (relay fields only; absent on an older center). */
export function nodeIssues(n: Pick<NodeInfo, 'status' | 'relay' | 'load' | 'clock'>): string[] {
  const out: string[] = []
  if (n.status !== 'online') return out
  if (n.load && n.load.cpu_pct >= 85) out.push(`CPU ${Math.round(n.load.cpu_pct)}%`)
  if (n.clock?.suspect) out.push(`clock ${n.clock.offset_ms > 0 ? '+' : ''}${n.clock.offset_ms.toFixed(0)} ms`)
  const legs = n.relay?.legs ?? n.load?.legs
  const up = n.relay?.legs !== undefined ? (n.relay.legs_up ?? 0) : n.load?.legs_up
  if (legs !== undefined && up !== undefined && legs > up) out.push(`${legs - up} of ${legs} legs not up`)
  if (n.relay?.enabled && !n.relay.in_peers) out.push('not in peers')
  if (n.relay && n.relay.routes_version_acked < n.relay.routes_version) out.push('routes not applied')
  return out
}

export function nodeView(n: Pick<NodeInfo, 'status' | 'relay' | 'load' | 'clock'>): NodeView {
  if (n.status === 'revoked') return 'revoked'
  if (n.status === 'offline') return 'offline'
  if (n.relay?.enabled && (n.relay.state === 'unregistered' || n.relay.state === 'registered')) return 'not_in_peers'
  return nodeIssues(n).length ? 'degraded' : 'online'
}

/** NodeRelay.state: the relay key and peer list of a node (Relay key block, In peers column). */
export const RELAY_STATE_SPEC: Record<NodeRelayState, Spec> = {
  ready: s('●', 'READY', 'live', 'solid', 'In the peers list, online, listener up: routes may use it'),
  in_peers: s('✓', 'IN PEERS', 'live', 'solid', 'Acknowledged the current peers list and is in it'),
  registered: s('○', 'PENDING PEERS', 'idle', 'dotted', 'Key registered; the node has not acknowledged the current peers list yet'),
  unregistered: s('○', 'NO KEY', 'idle', 'dotted', 'Relay on, but no relay key registered: the first one comes with the join or with Enable relay'),
  offline: s('✕', 'OFFLINE', 'fail', 'solid', 'The node is not connected'),
  unsupported: s('─', 'NO RELAY', 'idle', 'solid', 'The node runs without the relay (RELAY not on in its .env, or an older version)'),
  disabled: s('─', 'RELAY OFF', 'idle', 'solid', 'Relay is switched off for this node or the whole center'),
  revoked: s('■', 'REVOKED', 'idle', 'solid', 'Revoked: removed from the peers list'),
}

/**
 * Why a node is not (fully) in the routing network and what to do about it
 * (PROV-RELAY-1): null when there is nothing to do. action names the console
 * action: enable_relay (Host & agent → Enable relay, TOTP, same node id),
 * update_node (Host & agent → Update), revoke_stale (revoke the nodes that
 * never come back), wait.
 */
export type RelayAction = 'enable_relay' | 'update_node' | 'revoke_stale' | 'wait' | 'center_off'
export function relayNextStep(r: NodeRelay, centerOn = true): { why: string; action: RelayAction } | null {
  switch (r.state) {
    case 'revoked':
    case 'ready':
      break
    case 'disabled':
      if (!centerOn) return { why: 'The routing network is off for the whole center (RELAY_ENABLED).', action: 'center_off' }
      return r.enabled ? null : { why: 'Relay is off for this node: it does not take part in the routing network.', action: 'enable_relay' }
    case 'unsupported':
      return {
        why: 'The node does not offer the relay: it was started without RELAY=true (installed before the relay option) or runs an older release.',
        action: 'enable_relay',
      }
    case 'unregistered':
      return { why: 'The node presents a relay key the center has not registered (a hello never registers the first key).', action: 'enable_relay' }
    case 'offline':
      return { why: 'The node is not connected: it gets the current peers list when it comes back.', action: 'wait' }
    case 'registered':
      return { why: `Key registered; the node has not acknowledged peers list v${r.peers_version} yet (it is at v${r.peers_version_acked}).`, action: 'wait' }
  }
  if (r.peers_pending_offline?.length && r.peers_acked !== undefined && r.peers_total !== undefined && r.peers_acked < r.peers_total) {
    return {
      why: `${r.peers_pending_offline.length} node${r.peers_pending_offline.length === 1 ? ' is' : 's are'} in the peers list but offline: they trust this node only when they connect. A node that never comes back (an old identity) keeps every node short of full trust until it is revoked.`,
      action: 'revoke_stale',
    }
  }
  if (r.peers_acked !== undefined && r.peers_total !== undefined && r.peers_acked < r.peers_total) {
    return { why: 'Online nodes have not acknowledged the peers list with this node yet.', action: 'wait' }
  }
  return null
}

/** "In peers" for the node list: ✓ IN · v19 / ○ PENDING (v18 of v19) / ─ RELAY OFF. */
export function peersText(r: NodeRelay): { spec: Spec; text: string; sub?: string } {
  if (r.state === 'disabled' || r.state === 'unsupported' || r.state === 'revoked') return { spec: RELAY_STATE_SPEC[r.state], text: RELAY_STATE_SPEC[r.state].word }
  if (r.in_peers) return { spec: RELAY_STATE_SPEC.in_peers, text: `v${r.peers_version_acked}` }
  if (r.state === 'unregistered') return { spec: RELAY_STATE_SPEC.unregistered, text: 'NO KEY' }
  return { spec: RELAY_STATE_SPEC.registered, text: 'PENDING', sub: `v${r.peers_version_acked} of v${r.peers_version}` }
}

/* ── links ──────────────────────────────────────────────────────────────── */

export const LINK_SPEC: Record<LinkState, Spec> = {
  up: s('●', 'UP', 'live', 'solid'),
  degraded: s('▲', 'DEGRADED', 'warn', 'solid'),
  down: s('✕', 'DOWN', 'fail', 'solid'),
  idle: s('─', 'IDLE', 'idle', 'solid', 'No streams on this link'),
  unknown: s('○', 'NO DATA', 'idle', 'dotted', 'No report yet after the center restarted'),
}

/** RelayLink.state_reason in words. */
export const LINK_REASON: Record<RelayLink['state_reason'], string> = {
  '': '',
  dial_failed: 'cannot connect',
  tls_failed: 'TLS handshake failed',
  peer_rejected: 'the peer refused the connection',
  idle_timeout: 'no data within the idle timeout',
  high_rtt: 'RTT above twice its usual',
  retransmits: 'retransmits over 2%',
  resuming: 'a leg is resuming',
  node_offline: 'an end is offline',
}

/* ── routes ─────────────────────────────────────────────────────────────── */

export const ROUTE_SPEC: Record<RouteState, Spec> = {
  pending: s('◔', 'CONNECTING', 'start', 'dashed'),
  active: s('●', 'ACTIVE', 'live', 'solid'),
  degraded: s('▲', 'DEGRADED', 'warn', 'solid'),
  switching: s('◔', 'SWITCHING', 'start', 'dashed'),
  on_standby: s('▲', 'ON BACKUP', 'warn', 'solid', 'Running on a standby path after a failure'),
  no_path: s('✕', 'NO PATH', 'fail', 'solid', 'The stream does not reach the platforms through the relay'),
  ended: s('■', 'ENDED', 'idle', 'solid'),
}

export const HOP_SPEC: Record<HopState, Spec> = {
  connecting: s('◔', 'CONNECTING', 'start', 'dashed'),
  handshake: s('◔', 'HANDSHAKE', 'start', 'dashed'),
  offered: s('◔', 'OFFERED', 'start', 'dashed'),
  up: s('●', 'UP', 'live', 'solid'),
  resuming: s('↻', 'RESUMING', 'warn', 'dashed', 'Reconnecting and catching up from the ingress ring'),
  down: s('✕', 'DOWN', 'fail', 'solid'),
}

export const MODE_SPEC: Record<RelayRoute['mode'], Spec> = {
  auto: s('◇', 'AUTO', 'idle', 'solid', 'The controller may move this route'),
  pinned: s('⌖', 'PINNED', 'idle', 'solid', 'Only an operator moves this route; failover still works'),
}

export const EPOCH_SPEC: Record<RelayEpoch['state'], Spec> = {
  planned: s('○', 'STANDBY', 'idle', 'dotted'),
  active: s('●', 'ACTIVE', 'live', 'solid'),
  retired: s('■', 'RETIRED', 'idle', 'solid'),
  superseded: s('■', 'SUPERSEDED', 'idle', 'solid'),
}

/** Output states of the existing API in the console's look. */
export const OUTPUT_SPEC: Record<OutputState, Spec> = {
  pending: s('◔', 'PENDING', 'start', 'dashed'),
  waiting: s('○', 'WAITING', 'idle', 'dotted'),
  starting: s('◔', 'STARTING', 'start', 'dashed'),
  live: s('●', 'LIVE', 'live', 'solid'),
  reconnecting: s('↻', 'RECONNECTING', 'warn', 'dashed', 'Holding the platform session, retrying'),
  stopping: s('◔', 'STOPPING', 'start', 'dashed'),
  stopped: s('■', 'STOPPED', 'idle', 'solid'),
  failed: s('✕', 'FAILED', 'fail', 'solid'),
  off: s('─', 'OFF', 'idle', 'solid'),
  unknown: s('○', 'UNKNOWN', 'idle', 'dotted', 'Node unreachable, state unknown'),
}

export const CHANGED_BY: Record<RelayRoute['changed_by'], { word: string; cls: string }> = {
  operator: { word: 'operator', cls: 'text-text-soft' },
  controller: { word: 'controller', cls: 'text-muted' },
  failover: { word: 'failover', cls: 'text-warn' },
}

/** Why a stream has no relay route (NoPathStream.reason, route state_reason), in words. */
export const NO_PATH_TEXT: Record<NoPathReason, string> = {
  no_hub: 'no hub with relay on is ready',
  capacity: 'every hub already carries its maximum of relay legs',
  ingress_unsupported: 'the ingress node runs a version without relay',
  relay_disabled: 'relay is switched off (center or node)',
  ingress_not_ready: 'the ingress node has not acknowledged the current peers list',
}

/** A route's state_reason (controller code) in words; '' → none. */
export function reasonText(code: string): string {
  switch (code) {
    case '':
      return ''
    case 'hub_offline':
      return 'the hub stopped answering'
    case 'leg_down':
      return 'the hop is down'
    case 'resuming':
      return 'the hop is reconnecting and catching up from the ingress ring'
    case 'not_in_peers':
      return 'a node on the path is not in the peers list'
    case 'egress_lost':
      return 'the egress lost the stream'
    case 'failover':
      return 'failed over to a standby path'
    case 'switching':
      return 'a planned switch is running'
    default:
      return code in NO_PATH_TEXT ? NO_PATH_TEXT[code as NoPathReason] : code.replace(/_/g, ' ')
  }
}
